import { loadLocaleMessages } from "@/i18n/index"
import { loadDesktopThemeInMobile } from "@/plugins/loadDesktopThemeInMobile"
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordName
} from "vue-router"
import { ModalType } from "@/constants/modalConst"
import { isMobile } from "@common_lib/base/utils/src/tools"
import type { LocalCertification } from "@/stores/useWebsiteSettingStore"
import {
  resolveWebsiteErrorRoute,
  type WebsiteErrorStatus
} from "@/utils/resolveWebsiteErrorRoute"
import { consumeInitialRedirect } from "@/utils/initialRedirect"

type GuardResult = RouteLocationRaw | false | undefined

const BYPASS_ROUTE_NAMES: RouteRecordName[] = [
  "SystemMaintenance",
  "IPBlock",
  "404",
  "Horizontal",
  "AuthBlock"
]

const hasPendingApproval = ({
  isLoginWithHalf,
  certification
}: {
  isLoginWithHalf: boolean
  certification: LocalCertification
}): boolean =>
  isLoginWithHalf &&
  (certification.resetPassword || certification.termsVersion.length > 0)

const resolveFirstNavigation = (
  to: RouteLocationNormalized,
  {
    websiteErrorStatus,
    isRacingWebSite,
    isApprovalPending
  }: {
    websiteErrorStatus: WebsiteErrorStatus
    isRacingWebSite: boolean
    isApprovalPending: boolean
  }
): GuardResult => {
  // A pending ResetPassword / Terms step takes over this navigation; the approval
  // pages consume the target on exit instead, so the player still lands on it.
  const initialRedirect = isApprovalPending
    ? undefined
    : consumeInitialRedirect({ isRacingWebSite })

  const errorRoute = resolveWebsiteErrorRoute(websiteErrorStatus, to.name)

  if (errorRoute) return errorRoute
  if (to.name === "404" || to.name === "Horizontal") return { name: "Home" }

  return initialRedirect
}

const replaceDomain = (newDomain: string) => {
  const { protocol, host, hash } = window.location

  if (!host.includes(".")) return

  const parts = host.split(".")
  const newDomainParts = newDomain.split(".")

  if (parts.length > newDomainParts.length) {
    window.location.href = `${protocol}//${parts[0]}.${newDomain}/${hash}`
  } else {
    window.location.href = `${protocol}//${newDomain}/${hash}`
  }
}

const resolveApiAuthRedirect = (
  to: RouteLocationNormalized,
  {
    needAuthentication,
    isFromApi,
    isFake,
    isEnablePlayerNeedLogin
  }: {
    needAuthentication: boolean
    isFromApi: boolean
    isFake: boolean
    isEnablePlayerNeedLogin: boolean
  }
): GuardResult => {
  const requiresAuth = needAuthentication || to.name === "Login"
  const isApiOrigin = isFromApi || isFake

  if (!(requiresAuth && isApiOrigin)) return undefined

  if (isEnablePlayerNeedLogin && to.name !== "AuthBlock") return { name: "AuthBlock" }

  if (!isEnablePlayerNeedLogin) {
    if (isMobile()) {
      useModalStore().openModal(ModalType.LOGIN_TO_PROCEED)
      return false
    }
    return { name: "Home" }
  }

  return undefined
}

const resolveRegularAuthRedirect = (
  to: RouteLocationNormalized,
  {
    needAuthentication,
    isFromApi,
    isEnablePlayerNeedLogin,
    isPassLoginAPI
  }: {
    needAuthentication: boolean
    isFromApi: boolean
    isEnablePlayerNeedLogin: boolean
    isPassLoginAPI: boolean
  }
): GuardResult => {
  const unAuthRouteName = isEnablePlayerNeedLogin || isMobile() ? "Login" : "Home"

  if (
    needAuthentication &&
    !isFromApi &&
    to.name !== unAuthRouteName &&
    !isPassLoginAPI
  ) {
    return { name: unAuthRouteName }
  }

  return undefined
}

const resolveTermsRedirect = (
  to: RouteLocationNormalized,
  {
    isLoginWithHalf,
    certification
  }: {
    isLoginWithHalf: boolean
    certification: LocalCertification
  }
): GuardResult => {
  if (!hasPendingApproval({ isLoginWithHalf, certification })) return undefined

  if (certification.resetPassword) {
    return to.name === "ResetPassword" ? undefined : { name: "ResetPassword" }
  }

  return to.name === "Terms" ? undefined : { name: "Terms" }
}

const applyGuardResult = (next: NavigationGuardNext, result: GuardResult): void => {
  if (result === undefined) return next()
  if (result === false) return next(false)
  return next(result)
}

export default async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const websiteInfoStore = useWebsiteInfoStore()
  const { state: websiteInfoState, websiteErrorStatus } = storeToRefs(websiteInfoStore)
  const { isEnablePlayerNeedLogin, isFromApi, isFake, redirectDomain, isRacingWebSite } =
    websiteInfoState.value

  const authStore = useAuthStore()
  const websiteSettingStore = useWebsiteSettingStore()
  const { isLogin, certification } = storeToRefs(authStore)
  const { certificationLocalStorage } = storeToRefs(websiteSettingStore)
  const localCertification = certificationLocalStorage.value.localStorage.value

  const isLoginWithHalf = websiteInfoState.value.isTempRole

  if (from.name === undefined) {
    const firstNavigationResult = resolveFirstNavigation(to, {
      websiteErrorStatus: websiteErrorStatus.value,
      isRacingWebSite,
      isApprovalPending: hasPendingApproval({
        isLoginWithHalf,
        certification: localCertification
      })
    })
    if (firstNavigationResult !== undefined)
      return applyGuardResult(next, firstNavigationResult)
  }

  if (import.meta.env.PROD && redirectDomain) {
    replaceDomain(redirectDomain)
    return next(false)
  }

  await loadLocaleMessages(to)
  await loadDesktopThemeInMobile(to, from, websiteInfoState.value.webSiteCssName)

  if (!websiteInfoState.value.isEnableMobileReportPage) {
    await loadDesktopThemeInMobile(to, from, websiteInfoState.value.webSiteCssName)
  }

  if (BYPASS_ROUTE_NAMES.includes(to.name)) return next()

  const needAuthentication =
    (to.matched.some(record => record.meta.isNeedAuth) || isEnablePlayerNeedLogin) &&
    !isLogin.value &&
    !isLoginWithHalf

  const apiAuthResult = resolveApiAuthRedirect(to, {
    needAuthentication,
    isFromApi,
    isFake,
    isEnablePlayerNeedLogin
  })
  if (apiAuthResult !== undefined) return applyGuardResult(next, apiAuthResult)

  const regularAuthResult = resolveRegularAuthRedirect(to, {
    needAuthentication,
    isFromApi,
    isEnablePlayerNeedLogin,
    isPassLoginAPI: certification.value.isPassLoginAPI
  })
  if (regularAuthResult !== undefined) return applyGuardResult(next, regularAuthResult)

  if (isLoginWithHalf) {
    const { resetPassword, termsVersion } = localCertification
    if (resetPassword || termsVersion.length > 0) {
      authStore.updateCertification({ resetPassword, termsVersion })
    }
  }

  const termsResult = resolveTermsRedirect(to, {
    isLoginWithHalf,
    certification: localCertification
  })
  if (termsResult !== undefined) return applyGuardResult(next, termsResult)

  return next()
}
